import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { usePoll } from "@/lib/usePoll";
import {
  Bell, Clock, CalendarDays, ShieldAlert, Truck, ClipboardCheck, MessageSquare,
  Wrench, ArrowUpRight,
} from "lucide-react";

const TYPE_ICON = {
  timesheet_pending: Clock,
  timesheet_reviewed: Clock,
  leave_pending: CalendarDays,
  leave_reviewed: CalendarDays,
  safety_alert: ShieldAlert,
  damage_report: Truck,
  toolbox_talk: ClipboardCheck,
  pre_start_unsafe: Wrench,
  chat_mention: MessageSquare,
};

const SEV_DOT = {
  critical: "bg-red-500",
  warning: "bg-orange-500",
  info: "bg-blue-500",
  success: "bg-emerald-500",
};

// Tracks last-seen notification timestamp in localStorage so the badge only
// counts items newer than the user's last bell-open.
const SEEN_KEY = "fl_notif_last_seen";

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const recomputeUnread = (list) => {
    let lastSeen = "";
    try { lastSeen = localStorage.getItem(SEEN_KEY) || ""; } catch { /* noop */ }
    const count = list.filter((n) => (n.created_at || "") > lastSeen).length;
    setUnread(count);
  };

  const load = () => {
    api.get("/notifications")
      .then((r) => {
        const data = Array.isArray(r.data) ? r.data : [];
        setItems(data);
        recomputeUnread(data);
      })
      .catch(() => {});
  };

  usePoll(load, 30000);

  const markAllSeen = () => {
    try { localStorage.setItem(SEEN_KEY, new Date().toISOString()); } catch { /* noop */ }
    setUnread(0);
  };

  const onOpenChange = (v) => {
    setOpen(v);
    if (v) markAllSeen();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="notifications-bell"
          aria-label="Notifications"
          title="Notifications"
          className="relative w-9 h-9 rounded-sm flex items-center justify-center text-zinc-700 hover:text-[#FF5F15] hover:bg-zinc-100 dark:text-zinc-300 dark:hover:text-[#FF5F15] dark:hover:bg-zinc-800 transition-colors"
        >
          <Bell className="w-5 h-5" strokeWidth={2} />
          {unread > 0 && (
            <span
              data-testid="notifications-bell-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#FF5F15] text-black text-[9px] font-bold rounded-sm flex items-center justify-center tabular-nums"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-80 max-w-[calc(100vw-1rem)] p-0 rounded-sm border-zinc-200 dark:border-zinc-800 bg-white dark:bg-[#0F0F0F] text-zinc-900 dark:text-white overflow-hidden"
        data-testid="notifications-popover"
      >
        <div className="px-4 py-2.5 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500 font-mono">
            // Notifications
          </div>
          {items.length > 0 && (
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">
              {items.length} total
            </div>
          )}
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-zinc-500" data-testid="notifications-empty">
              All caught up.
            </div>
          ) : (
            items.slice(0, 8).map((n) => {
              const Icon = TYPE_ICON[n.type] || Bell;
              return (
                <Link
                  to={n.link || "/app/inbox"}
                  key={n.id}
                  onClick={() => setOpen(false)}
                  data-testid={`notifications-item-${n.id}`}
                  className="flex items-start gap-3 px-4 py-3 border-b border-zinc-100 dark:border-zinc-900 last:border-b-0 hover:bg-zinc-50 dark:hover:bg-[#151C27] transition-colors"
                >
                  <div className="relative shrink-0 mt-0.5">
                    <Icon className="w-4 h-4 text-zinc-500 dark:text-zinc-400" strokeWidth={2} />
                    <span className={`absolute -top-1 -right-1.5 w-2 h-2 rounded-full ${SEV_DOT[n.severity] || "bg-zinc-400"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold truncate">{n.title}</div>
                    {n.subtitle && (
                      <div className="text-[11px] text-zinc-500 dark:text-zinc-400 truncate">{n.subtitle}</div>
                    )}
                    <div className="text-[9px] font-mono text-zinc-400 mt-1 uppercase tracking-widest">
                      {timeAgo(n.created_at)}
                    </div>
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                </Link>
              );
            })
          )}
        </div>

        <Link
          to="/app/inbox"
          onClick={() => setOpen(false)}
          data-testid="notifications-view-all"
          className="block px-4 py-2.5 text-center border-t border-zinc-200 dark:border-zinc-800 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF5F15] hover:bg-zinc-50 dark:hover:bg-[#151C27] transition-colors"
        >
          View full inbox →
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "short" });
}
